import { useEffect, useState } from "react";
import { useNavigate, useParams, Link, useLocation } from "react-router-dom";
import { api, API_BASE } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { formatMoney, formatDate } from "@/lib/format";
import { ArrowLeft, Download, Pencil, Trash2, Plus } from "lucide-react";
import TransactionsTable from "@/components/TransactionsTable";
import TransactionDialog from "@/components/TransactionDialog";
import AttachmentsSection from "@/components/AttachmentsSection";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { toast } from "sonner";

const CFG = {
  sales: { endpoint: "/sales-invoices", editRoute: "/satis-faturalari/duzenle", listRoute: "/satis-faturalari", title: "Satış Faturası", entityRoute: "/musteriler",
           txPreset: (inv) => ({ category:"receipt", from_kind:"customer", from_id:inv.entity_id, to_kind:"cash_register", currency:inv.currency, invoice_endpoint: `/sales-invoices/${inv.id}/payment` }) },
  purchase: { endpoint: "/purchase-invoices", editRoute: "/alis-faturalari/duzenle", listRoute: "/alis-faturalari", title: "Alış Faturası", entityRoute: "/tedarikciler",
              txPreset: (inv) => ({ category:"payment", from_kind:"cash_register", to_kind:"supplier", to_id:inv.entity_id, currency:inv.currency, invoice_endpoint: `/purchase-invoices/${inv.id}/payment` }) },
};

export default function InvoiceDetail({ kind }) {
  const cfg = CFG[kind];
  const { id } = useParams();
  const nav = useNavigate();
  const location = useLocation();
  const backTo = location.state?.returnTo || cfg.listRoute;
  const [inv, setInv] = useState(null);
  const [txs, setTxs] = useState([]);
  const [txOpen, setTxOpen] = useState(false);
  const [delOpen, setDelOpen] = useState(false);

  const load = async () => {
    const [i, t] = await Promise.all([
      api.get(`${cfg.endpoint}/${id}`),
      api.get("/transactions"),
    ]);
    setInv(i.data);
    setTxs(t.data.filter(x=>x.invoice_id === id));
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [id, kind]);

  const doDelete = async (deletePayments) => {
    try {
      await api.delete(`${cfg.endpoint}/${id}`, { params: { delete_payments: deletePayments } });
      toast.success("Fatura silindi");
      nav(backTo);
    } catch { toast.error("Silinemedi"); }
  };

  const openPdf = () => { window.open(`${API_BASE}${cfg.endpoint}/${id}/pdf`, "_blank"); };

  if (!inv) return <div className="text-zinc-500">Yükleniyor...</div>;
  const paidTxs = txs.filter(t => t.category !== "invoice_sale" && t.category !== "invoice_purchase");
  const paidAmount = paidTxs.reduce((s,t)=>s+t.amount, 0);
  return (
    <div className="space-y-6" data-testid={`${kind}-invoice-detail`}>
      <div className="flex items-center gap-3">
        <Button variant="ghost" size="sm" onClick={()=>nav(backTo)} data-testid="invoice-back-button"><ArrowLeft size={16}/></Button>
        <div className="flex-1">
          <div className="text-xs uppercase tracking-widest text-zinc-500">{cfg.title}</div>
          <h1 className="text-3xl mt-1">{inv.invoice_number}</h1>
        </div>
        <Button variant="outline" onClick={openPdf} data-testid="pdf-btn"><Download size={16} className="mr-1"/>PDF</Button>
        <Button variant="outline" onClick={()=>nav(`${cfg.editRoute}/${id}`, { state: { returnTo: backTo } })}><Pencil size={16} className="mr-1"/>Düzenle</Button>
        <Button variant="outline" onClick={()=>setDelOpen(true)}><Trash2 size={16} className="mr-1"/>Sil</Button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <div className="bg-white p-5 border border-zinc-200 rounded-md">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Cari</div>
          <Link to={`${cfg.entityRoute}/${inv.entity_id}`} className="mt-2 block font-medium hover:underline" data-testid="invoice-entity-link">{inv.entity_name}</Link>
          <div className="text-xs text-zinc-500 mt-1">{formatDate(inv.date)}</div>
        </div>
        <div className="bg-white p-5 border border-zinc-200 rounded-md">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Toplam</div>
          <div className="text-2xl number mt-2">{formatMoney(inv.total, inv.currency)}</div>
        </div>
        <div className="bg-white p-5 border border-zinc-200 rounded-md">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Ödenen</div>
          <div className="text-2xl number mt-2 pos">{formatMoney(paidAmount, inv.currency)}</div>
        </div>
        <div className="bg-white p-5 border border-zinc-200 rounded-md">
          <div className="text-xs uppercase tracking-widest text-zinc-500">Kalan</div>
          <div className={`text-2xl number mt-2 ${(inv.total-paidAmount)>0?"neg":"pos"}`}>{formatMoney(inv.total - paidAmount, inv.currency)}</div>
        </div>
      </div>

      <div className="bg-white border border-zinc-200 rounded-md overflow-hidden">
        <table className="acc">
          <thead><tr><th>Ürün</th><th className="num">Miktar</th><th>Birim</th><th className="num">Birim Fiyat</th><th className="num">KDV%</th><th className="num">Toplam</th></tr></thead>
          <tbody>
            {inv.items.map((it, idx)=>(
              <tr key={idx}>
                <td>{it.name}</td>
                <td className="num">{it.quantity}</td>
                <td>{it.unit}</td>
                <td className="num">{formatMoney(it.unit_price, inv.currency)}</td>
                <td className="num">{it.vat_rate}%</td>
                <td className="num">{formatMoney(it.total_with_vat, inv.currency)}</td>
              </tr>
            ))}
          </tbody>
        </table>
        <div className="border-t border-zinc-200 p-4 flex justify-end">
          <div className="w-full md:w-80 space-y-1 text-sm">
            <div className="flex justify-between"><span className="text-zinc-500">Ara Toplam</span><span className="number">{formatMoney(inv.subtotal || 0, inv.currency)}</span></div>
            {(inv.discount_amount || 0) > 0 && (
              <div className="flex justify-between" data-testid="invoice-detail-discount">
                <span className="text-zinc-500">
                  İskonto{inv.discount_type === "percent" ? ` (%${inv.discount_value})` : inv.discount_base === "gross" ? " (KDV Dahil)" : ""}
                </span>
                <span className="number">-{formatMoney(inv.discount_amount, inv.currency)}</span>
              </div>
            )}
            <div className="flex justify-between"><span className="text-zinc-500">KDV</span><span className="number">{formatMoney(inv.vat_total || 0, inv.currency)}</span></div>
            <div className="flex justify-between text-base font-medium pt-1 border-t border-zinc-200"><span>Genel Toplam</span><span className="number">{formatMoney(inv.total, inv.currency)}</span></div>
          </div>
        </div>
      </div>

      <div className="flex items-center justify-between">
        <h2 className="text-xl">{kind==="sales"?"Tahsilatlar":"Ödemeler"}</h2>
        <Button onClick={()=>setTxOpen(true)} className="bg-zinc-900 hover:bg-zinc-800" data-testid="add-invoice-payment-btn"><Plus size={16} className="mr-1"/>{kind==="sales"?"Tahsilat":"Ödeme"} Ekle</Button>
      </div>
      <TransactionsTable items={paidTxs} onChanged={load} />

      <TransactionDialog open={txOpen} onClose={()=>setTxOpen(false)} onSaved={load} preset={cfg.txPreset(inv)} title={kind==="sales"?"Fatura Tahsilatı":"Fatura Ödemesi"} />

      <AttachmentsSection
        ownerKind={kind === "sales" ? "sales_invoice" : "purchase_invoice"}
        ownerId={inv.id}
        title="Fatura Ek Belgeleri"
        accept="image/*,application/pdf"
        testIdPrefix="invoice-attachments"
      />

      <AlertDialog open={delOpen} onOpenChange={setDelOpen}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Fatura silinsin mi?</AlertDialogTitle>
            <AlertDialogDescription>
              Bu fatura üzerinden {paidTxs.length} ödeme/tahsilat işlemi yapılmış. Fatura silinince bu işlemler de silinsin mi?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Vazgeç</AlertDialogCancel>
            <Button variant="outline" onClick={()=>doDelete(false)} data-testid="delete-keep-payments">Sadece Faturayı Sil</Button>
            <AlertDialogAction onClick={()=>doDelete(true)} data-testid="delete-with-payments" className="bg-red-700 hover:bg-red-800">Fatura + İşlemleri Sil</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
